import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import usePostCashQR from "../../apis/usePostCashQR/usePostCashQR";
import { ITax } from "../../apis/useSubmitCheckout/types";
import { PAYMENT_METHODS } from "../../apis/useSubmitPayment/types";
import useSubmitPayment from "../../apis/useSubmitPayment/useSubmitPayment";
import useTerminalManagement from "../../apis/useTerminalManagement";
import { displayCentsWithDollarSign } from "../../common/helpers/stringUtils";
import Condition from "../../components/Conditional/Condition";
import Overlay from "../../components/Overlay/Overlay";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import "./PaymentPage.scss";
import { PAYMENT_METHOD_CONFIG, PAYMENT_PAGE_LAYOUT } from "./paymentMethodConfig";

const SERVICE_CHARGE_REGEX = /service\s*charge/i;
const GST_REGEX = /gst/i;

const findTaxEntry = (tax: ITax[] | undefined, regex: RegExp): ITax | undefined =>
	tax?.find((entry) => regex.test(entry.name ?? "") || regex.test(entry.type ?? ""));

const toRatePercent = (rate: number | undefined): number => (rate == null ? 0 : Math.round(rate));

const PaymentPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const {
		state: { paymentData, paymentReference },
	} = usePaymentContext((state) => state);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);
	const {
		submitPayment,
		isLoading,
		currentMethod,
		error,
		clearError,
		isChargedButFailed,
		clearChargedError,
	} = useSubmitPayment();
	const { submitCashQR, isLoading: isCashLoading, error: cashError } = usePostCashQR();
	const [cashErrorMsg, setCashErrorMsg] = useState<string | null>(null);
	const [pageIndex, setPageIndex] = useState(0);
	const terminalSubmitStartRef = useRef<number | null>(null);
	const terminalSubmitMethodRef = useRef<PAYMENT_METHODS | null>(null);
	const lastErrorRef = useRef<string | null>(null);

	// Use the new terminal management hook
	// const { checkStatusAndResetTerminal } = useTerminalManagement({
	// onLogonSuccess: () => {
	// console.log("✅ Terminal logon successful - ready for payment");
	// },
	// maxRetries: 1,
	// retryDelay: 1500,
	// });

	useEffect(() => {
		addDatadogAction("payment_page_mounted", {
			hasPaymentReference: !!paymentReference,
			hasPaymentData: !!paymentData,
		});
	}, []);

	useEffect(() => {
		if (paymentReference) {
			// checkStatusAndResetTerminal();
		} else {
			addDatadogAction("payment_page_redirect_to_cart", {
				paymentReference: null,
				hasPaymentData: !!paymentData,
			});
			console.log("⚠️ No payment reference found, redirecting to cart");
			navigate(PATH.CART);
		}
	}, [paymentReference]);

	useEffect(() => {
		if (paymentData === null || paymentData === undefined) {
			addDatadogAction("payment_page_null_payment_data", {
				hasPaymentReference: !!paymentReference,
			});
		}
	}, [paymentData]);

	useEffect(() => {
		if (cashError) {
			setCashErrorMsg(cashError);
		}
	}, [cashError]);

	// Fire payment_terminal_result_received once per submit cycle. Submit cycle
	// ends when isLoading flips from true → false. Result classification:
	// - error present → "failed"
	// - otherwise → "success"
	useEffect(() => {
		if (terminalSubmitStartRef.current == null) return;
		if (isLoading) return;

		const durationMs = performance.now() - terminalSubmitStartRef.current;
		const method = terminalSubmitMethodRef.current;

		// Reset BEFORE firing so a re-entrant render doesn't double-emit.
		terminalSubmitStartRef.current = null;
		terminalSubmitMethodRef.current = null;

		const status: "success" | "failed" = error ? "failed" : "success";

		addDatadogAction("payment_terminal_result_received", {
			...buildLocationContext(location, stall),
			method,
			status,
			durationMs,
		});
	}, [isLoading, error, location, stall]);

	// Fire payment_error_dialog_opened when error or cashErrorMsg transitions
	// from falsy to truthy.
	useEffect(() => {
		const code = error ?? cashErrorMsg;

		if (!code) {
			lastErrorRef.current = null;

			return;
		}
		if (lastErrorRef.current === code) return;

		lastErrorRef.current = code;
		addDatadogAction("payment_error_dialog_opened", {
			...buildLocationContext(location, stall),
			errorCode: code,
			errorMessage: code,
		});
	}, [error, cashErrorMsg, location, stall]);

	const handleGoBack = useCallback(() => {
		addDatadogAction("payment_back_to_cart", {
			...buildLocationContext(location, stall),
			paymentReference,
		});
		navigate(PATH.CART);
	}, [navigate, location, stall, paymentReference]);

	const onPaymentMethodClick = useCallback(
		async (method: PAYMENT_METHODS) => {
			addDatadogAction("payment_method_clicked", {
				...buildLocationContext(location, stall),
				method,
				paymentReference,
			});

			if (method === PAYMENT_METHODS.CASH) {
				addDatadogAction("payment_navigate_to_cash_qr", {
					...buildLocationContext(location, stall),
				});
				const qrContent = await submitCashQR(
					paymentReference ?? "",
					paymentData?.orders_figures?.net_value ?? 0
				);

				if (qrContent) {
					navigate(PATH.CASH_QR, { state: { qrContent } });
				}

				return;
			}

			// Record submit-start so we can compute durationMs on result.
			terminalSubmitStartRef.current = performance.now();
			terminalSubmitMethodRef.current = method;
			addDatadogAction("payment_terminal_submit_start", {
				...buildLocationContext(location, stall),
				method,
				paymentReference,
			});

			submitPayment(method);
		},
		[submitPayment, submitCashQR, paymentReference, navigate, location, stall, paymentData]
	);

	const handleCloseErrorDialog = useCallback(() => {
		const code = lastErrorRef.current;

		addDatadogAction("payment_error_dialog_dismissed", {
			...buildLocationContext(location, stall),
			errorCode: code,
		});
		clearError();
		setCashErrorMsg(null);
	}, [clearError, location, stall]);

	const goToNextPage = useCallback(() => {
		addDatadogAction("payment_page_navigation", {
			...buildLocationContext(location, stall),
			from: 0,
			to: 1,
		});
		setPageIndex(1);
	}, [location, stall]);
	const goToPrevPage = useCallback(() => {
		addDatadogAction("payment_page_navigation", {
			...buildLocationContext(location, stall),
			from: 1,
			to: 0,
		});
		setPageIndex(0);
	}, [location, stall]);

	const serviceChargeTax = useMemo(
		() => findTaxEntry(paymentData?.tax, SERVICE_CHARGE_REGEX),
		[paymentData?.tax]
	);
	const gstTax = useMemo(() => findTaxEntry(paymentData?.tax, GST_REGEX), [paymentData?.tax]);

	if (!paymentData) {
		return null;
	}

	const bcrsDepositAmount = paymentData.orders_figures.bcrs_total ?? 0;

	const currentLayout = PAYMENT_PAGE_LAYOUT[pageIndex];
	const hasNextPage = pageIndex < PAYMENT_PAGE_LAYOUT.length - 1;
	const hasPrevPage = pageIndex > 0;
	const anyLoading = isLoading || isCashLoading;

	return (
		<div className="PaymentPage">
			<div className="PaymentHeader">
				<button className="BackButton" onClick={handleGoBack}>
					←
				</button>
			</div>

			<div className="PaymentContent">
				<h1 className="PaymentTitle">{t("paymentPage.selectMethod")}</h1>
				<h1 className="PaymentAmount">
					{displayCentsWithDollarSign(paymentData.orders_figures.net_value)}
				</h1>
				<div className="PaymentDetailSection">
					<div className="TotalSection">
						<div className="TotalLine">
							<span>{t("paymentPage.subtotal")}</span>
							<span>
								{displayCentsWithDollarSign(
									paymentData.orders_figures.subtotal_value - bcrsDepositAmount
								)}
							</span>
						</div>
						<Condition check={!!serviceChargeTax}>
							<div className="TotalLine">
								<span>
									{t("paymentPage.serviceCharge", {
										rate: toRatePercent(serviceChargeTax?.rate),
									})}
								</span>
								<span>{displayCentsWithDollarSign(serviceChargeTax?.amount ?? 0)}</span>
							</div>
						</Condition>
						<Condition check={!!gstTax}>
							<div className="TotalLine">
								<span>{t("paymentPage.gst")}</span>
								<span>{displayCentsWithDollarSign(gstTax?.amount ?? 0)}</span>
							</div>
						</Condition>
						<Condition check={bcrsDepositAmount > 0}>
							<div className="TotalLine BcrsRow">
								<span>{t("paymentPage.bcrsDeposit")}</span>
								<span>{displayCentsWithDollarSign(bcrsDepositAmount)}</span>
							</div>
							<div className="BcrsDisclaimer">{t("cartPage.bcrs.disclaimer")}</div>
						</Condition>
						<div className="TotalLine Total">
							<span>{t("paymentPage.total")}</span>
							<span>{displayCentsWithDollarSign(paymentData.orders_figures.net_value)}</span>
						</div>
					</div>
				</div>
				<div className="PaymentMethodSection">
					<div className="PaymentGrid">
						{currentLayout.map((method, cellIndex) => {
							if (!method) {
								return <div key={`empty-${cellIndex}`} className="PaymentMethodItem" />;
							}

							const config = PAYMENT_METHOD_CONFIG[method];

							if (!config) {
								return <div key={`empty-${cellIndex}`} className="PaymentMethodItem" />;
							}

							return (
								<button
									key={method}
									className="PaymentMethodItem"
									onClick={() => onPaymentMethodClick(method)}
									disabled={anyLoading}
								>
									<div className="PaymentMethodWithLogo">
										{config.icons?.map((icon, i) => (
											<img
												key={i}
												src={icon}
												alt={config.label}
												className="PaymentMethodLogo"
												draggable={false}
											/>
										))}
										{config.buttonLabel && (
											<span className="PaymentMethodLabel">{config.buttonLabel}</span>
										)}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			</div>

			{hasPrevPage && (
				<button
					className="PaymentPageNavButton PaymentPageNavButton--prev"
					onClick={goToPrevPage}
					disabled={anyLoading}
					aria-label="Previous page"
					data-testid="payment-page-prev"
				>
					‹
				</button>
			)}
			{hasNextPage && (
				<button
					className="PaymentPageNavButton PaymentPageNavButton--next"
					onClick={goToNextPage}
					disabled={anyLoading}
					aria-label="Next page"
					data-testid="payment-page-next"
				>
					›
				</button>
			)}

			{anyLoading && (
				<Overlay
					line1={
						isCashLoading
							? t("paymentPage.processingCash")
							: currentMethod && PAYMENT_METHOD_CONFIG[currentMethod]
								? t("paymentPage.processingMethod", {
										label: PAYMENT_METHOD_CONFIG[currentMethod].label,
									})
								: t("paymentPage.processing")
					}
					line2={t("paymentPage.dontCloseWait")}
					enableDotAnimation={true}
				/>
			)}

			<PopupDialog
				data-testid="payment-error-dialog"
				open={!!(error || cashErrorMsg)}
				title={t("paymentPage.failedTitle")}
				content={
					<div>
						<p>{t("paymentPage.failedMessage")}</p>
					</div>
				}
				onClose={handleCloseErrorDialog}
				buttons={[
					{
						text: t("paymentPage.close"),
						onClick: handleCloseErrorDialog,
					},
				]}
			/>

			<PopupDialog
				data-testid="payment-charged-failed-dialog"
				open={isChargedButFailed}
				title={t("paymentPage.chargedFailedTitle")}
				content={
					<div>
						<p>{t("paymentPage.chargedFailedMessage")}</p>
					</div>
				}
				onClose={clearChargedError}
				buttons={[
					{
						text: t("paymentPage.close"),
						onClick: clearChargedError,
					},
				]}
			/>
		</div>
	);
};

export default PaymentPage;
